import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Eye, RefreshCw, Trash2, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import MultiSelect from '@/components/ui/MultiSelect';
import TmdbContentAutocomplete from '@/components/forms/TmdbContentAutocomplete';
import ValidatedEpisodeLinkInput, {
  EpisodeLinkValidationState,
} from '@/components/forms/ValidatedEpisodeLinkInput';
import { languageOptions } from '@/constants';
import { useCreateBulkEpisodes, usePreviewBulkEpisodes, useValidateBulkEpisodeLinks } from '@/hooks/useTVShows';
import { useTranslation } from '@/hooks';
import {
  BulkEpisodePreviewRequest,
  BulkEpisodeQuality,
  CreateEpisodeRequest,
  Episode,
  Language,
  MediaLink,
  TVShow,
} from '@/types/model';
import { fillEpisodeLinkTemplate, inferEpisodeLinkTemplate } from '@/utils/episodeLinkTemplate';

interface BulkEpisodeImportProps {
  tvShow: TVShow;
  existingEpisodes: Episode[];
  source?: 'tmdb' | 'generated';
  onCancel: () => void;
  onSuccess: () => void;
  onLinkTVShowToTmdb?: (tmdbId: string) => Promise<void>;
}

const qualityOptions: Array<{ value: BulkEpisodeQuality; label: string }> = [
  { value: 'SD', label: 'SD (480p)' },
  { value: 'HD', label: 'HD (720p)' },
  { value: 'FHD', label: 'Full HD (1080p)' },
  { value: 'UHD', label: '4K (2160p)' },
];

const fieldClass = 'w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500';
const episodePlaceholderPattern = /\[e{1,3}\]/;

const rowKey = (episode: CreateEpisodeRequest) => episode.tmdbId || `${episode.seasonNumber}-${episode.episodeNumber}`;

const BulkEpisodeImport = ({
  tvShow,
  existingEpisodes,
  source = 'tmdb',
  onCancel,
  onSuccess,
  onLinkTVShowToTmdb,
}: BulkEpisodeImportProps) => {
  const { t } = useTranslation();
  const labels = t.bulkEpisodes;
  const previewMutation = usePreviewBulkEpisodes();
  const createMutation = useCreateBulkEpisodes();
  const linkValidationMutation = useValidateBulkEpisodeLinks();
  const [request, setRequest] = useState<BulkEpisodePreviewRequest>({
    tmdbId: tvShow.tmdbId || '',
    seasonFrom: 1,
    seasonTo: 1,
    episodeFrom: 1,
    episodeTo: 20,
    linkTemplate: inferEpisodeLinkTemplate(existingEpisodes),
    quality: 'FHD',
    audioLanguages: ['SPANISH'],
    subtitleLanguages: [],
  });
  const [episodes, setEpisodes] = useState<CreateEpisodeRequest[]>([]);
  const [englishTitleTemplate, setEnglishTitleTemplate] = useState('Episode #');
  const [spanishTitleTemplate, setSpanishTitleTemplate] = useState('Capítulo #');
  const [defaultDuration, setDefaultDuration] = useState(45);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [existingCount, setExistingCount] = useState(0);
  const [detailsIndex, setDetailsIndex] = useState<number | null>(null);
  const [linkValidations, setLinkValidations] = useState<Record<string, EpisodeLinkValidationState>>({});
  const [isTvShowLinked, setIsTvShowLinked] = useState(Boolean(tvShow.tmdbId));
  const [isLinkingTvShow, setIsLinkingTvShow] = useState(false);
  const validationTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => () => {
    validationTimers.current.forEach((timer) => clearTimeout(timer));
    validationTimers.current.clear();
  }, []);

  const selectedEpisodes = useMemo(
    () => episodes.filter((episode) => selectedKeys.has(rowKey(episode))),
    [episodes, selectedKeys],
  );
  const hasValidEpisodePlaceholder = episodePlaceholderPattern.test(request.linkTemplate);
  const hasSeasonPlaceholder = /\[s{1,2}\]/.test(request.linkTemplate);
  const hasValidTitleTemplates = englishTitleTemplate.includes('#') && spanishTitleTemplate.includes('#');
  const linkTemplateError = request.linkTemplate.trim()
    ? !hasValidEpisodePlaceholder
      ? labels.missingEpisodePlaceholder
      : request.seasonFrom < request.seasonTo && !hasSeasonPlaceholder
        ? labels.missingSeasonPlaceholder
        : undefined
    : undefined;
  const isPreviewLoading = previewMutation.isPending || isLinkingTvShow;

  const setRequestField = <K extends keyof BulkEpisodePreviewRequest>(
    field: K,
    value: BulkEpisodePreviewRequest[K],
  ) => setRequest((current) => ({ ...current, [field]: value }));

  const updateEpisode = <K extends keyof CreateEpisodeRequest>(
    index: number,
    field: K,
    value: CreateEpisodeRequest[K],
  ) => {
    setEpisodes((current) => current.map((episode, rowIndex) => (
      rowIndex === index ? { ...episode, [field]: value } : episode
    )));
  };

  const validateUrls = async (urls: string[]) => {
    const uniqueUrls = [...new Set(urls.filter(Boolean))];
    if (uniqueUrls.length === 0) return;
    setLinkValidations((current) => {
      const next = { ...current };
      uniqueUrls.forEach((url) => { next[url] = { status: 'checking' }; });
      return next;
    });

    try {
      const results = await linkValidationMutation.mutateAsync({ tvShowId: tvShow.id, urls: uniqueUrls });
      const resultsByUrl = new Map(results.map((result) => [result.url, result]));
      setLinkValidations((current) => {
        const next = { ...current };
        uniqueUrls.forEach((url) => {
          const result = resultsByUrl.get(url);
          next[url] = result?.valid
            ? { status: 'valid' }
            : { status: 'invalid', issue: result?.issue || labels.linkValidationError };
        });
        return next;
      });
    } catch {
      setLinkValidations((current) => {
        const next = { ...current };
        uniqueUrls.forEach((url) => { next[url] = { status: 'invalid', issue: labels.linkValidationError }; });
        return next;
      });
    }
  };

  const updateLink = (index: number, url: string) => {
    setEpisodes((current) => current.map((episode, rowIndex) => {
      if (rowIndex !== index) return episode;
      const links = [...(episode.links || [])];
      if (links[0]) links[0] = { ...links[0], url };
      return { ...episode, links };
    }));

    const key = rowKey(episodes[index]);
    const currentTimer = validationTimers.current.get(key);
    if (currentTimer) clearTimeout(currentTimer);
    if (!url.trim()) {
      setLinkValidations((current) => ({
        ...current,
        [url]: { status: 'invalid', issue: labels.linkValidationError },
      }));
      return;
    }
    setLinkValidations((current) => ({ ...current, [url]: { status: 'checking' } }));
    validationTimers.current.set(key, setTimeout(() => {
      validationTimers.current.delete(key);
      void validateUrls([url]);
    }, 600));
  };

  const handlePreview = async () => {
    if (request.seasonFrom > request.seasonTo || request.episodeFrom > request.episodeTo) {
      toast.error(labels.invalidRange);
      return;
    }
    const requestedCount = (request.seasonTo - request.seasonFrom + 1)
      * (request.episodeTo - request.episodeFrom + 1);
    if (requestedCount > 200) {
      toast.error(labels.tooMany);
      return;
    }
    if (!hasValidEpisodePlaceholder) {
      toast.error(labels.missingEpisodePlaceholder);
      return;
    }
    if (request.seasonFrom < request.seasonTo && !hasSeasonPlaceholder) {
      toast.error(labels.missingSeasonPlaceholder);
      return;
    }
    if (source === 'generated' && !hasValidTitleTemplates) {
      toast.error(labels.missingTitlePlaceholder);
      return;
    }
    if (source === 'generated' && (defaultDuration < 1 || defaultDuration > 300)) {
      toast.error(labels.invalidDuration);
      return;
    }
    try {
      if (source === 'generated') {
        const existingPositions = new Set(existingEpisodes.map((episode) => (
          `${episode.seasonNumber}-${episode.episodeNumber}`
        )));
        const now = new Date().toISOString();
        const generated: CreateEpisodeRequest[] = [];
        let omittedCount = 0;

        for (let season = request.seasonFrom; season <= request.seasonTo; season += 1) {
          for (let episodeNumber = request.episodeFrom; episodeNumber <= request.episodeTo; episodeNumber += 1) {
            if (existingPositions.has(`${season}-${episodeNumber}`)) {
              omittedCount += 1;
              continue;
            }
            generated.push({
              tvShowId: tvShow.id,
              seasonNumber: season,
              episodeNumber,
              title: englishTitleTemplate.split('#').join(String(episodeNumber)),
              spanishTitle: spanishTitleTemplate.split('#').join(String(episodeNumber)),
              durationMinutes: defaultDuration,
              rating: 0,
              active: true,
              links: [{
                id: crypto.randomUUID(),
                quality: request.quality,
                url: fillEpisodeLinkTemplate(request.linkTemplate, season, episodeNumber),
                audioLanguages: request.audioLanguages,
                subtitleLanguages: request.subtitleLanguages,
                active: true,
                createdDate: now,
              }],
            });
          }
        }

        setEpisodes(generated);
        setSelectedKeys(new Set(generated.map(rowKey)));
        setExistingCount(omittedCount);
        void validateUrls(generated.map((episode) => episode.links?.[0]?.url || ''));
        if (generated.length === 0) toast.error(labels.noEpisodes);
        return;
      }

      if (!isTvShowLinked && onLinkTVShowToTmdb) {
        setIsLinkingTvShow(true);
        await onLinkTVShowToTmdb(request.tmdbId);
        setIsTvShowLinked(true);
      }
      const response = await previewMutation.mutateAsync({ tvShowId: tvShow.id, request });
      const now = new Date().toISOString();
      const normalized = response.episodes.map((episode) => ({
        ...episode,
        tvShowId: tvShow.id,
        links: (episode.links || []).map((link) => ({
          ...link,
          id: link.id || crypto.randomUUID(),
          createdDate: link.createdDate || now,
        })) as MediaLink[],
      }));
      setEpisodes(normalized);
      setSelectedKeys(new Set(normalized.map(rowKey)));
      setExistingCount(response.existingCount);
      void validateUrls(normalized.map((episode) => episode.links?.[0]?.url || ''));
      if (normalized.length === 0) toast.error(labels.noEpisodes);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : labels.previewError);
    } finally {
      setIsLinkingTvShow(false);
    }
  };

  const handleCreate = async () => {
    const hasIncompleteEpisode = selectedEpisodes.some((episode) => (
      !episode.title.trim()
      || !episode.durationMinutes
      || episode.durationMinutes < 1
      || !episode.links?.[0]?.url
    ));
    if (hasIncompleteEpisode) {
      toast.error(labels.incompleteEpisode);
      return;
    }
    const selectedLinkStates = selectedEpisodes.map((episode) => (
      linkValidations[episode.links?.[0]?.url || '']
    ));
    if (selectedLinkStates.some((state) => !state || state.status === 'checking')) {
      toast.error(labels.validationPending);
      return;
    }
    if (selectedLinkStates.some((state) => state.status === 'invalid')) {
      toast.error(labels.invalidLinks);
      return;
    }
    const positions = selectedEpisodes.map((episode) => `${episode.seasonNumber}-${episode.episodeNumber}`);
    if (new Set(positions).size !== positions.length) {
      toast.error(labels.duplicatePosition);
      return;
    }
    try {
      const response = await createMutation.mutateAsync({
        tvShowId: tvShow.id,
        episodes: selectedEpisodes,
      });
      toast.success(labels.created.replace('{count}', String(response.count)));
      onSuccess();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : labels.createError);
    }
  };

  const toggleSelected = (episode: CreateEpisodeRequest) => {
    const key = rowKey(episode);
    setSelectedKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const removeEpisode = (index: number) => {
    const removed = episodes[index];
    setEpisodes((current) => current.filter((_, rowIndex) => rowIndex !== index));
    setSelectedKeys((current) => {
      const next = new Set(current);
      next.delete(rowKey(removed));
      return next;
    });
  };

  const detailsEpisode = detailsIndex == null ? null : episodes[detailsIndex];

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="mx-auto max-w-[1800px] px-4 sm:px-6 lg:px-8">
        <Button variant="link" onClick={onCancel}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {labels.back}
        </Button>

        <section className="border-y border-gray-200 bg-white px-4 py-5 sm:px-6">
          <div className="mb-5">
            <h1 className="text-xl font-semibold text-gray-900">
              {source === 'generated' ? labels.generatedTitle : labels.title}
            </h1>
            <p className="mt-1 text-sm text-gray-600">{tvShow.title}</p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-7">
            {source === 'tmdb' && <div className="md:col-span-2 xl:col-span-2">
              {tvShow.tmdbId ? (
                <Input
                  label={labels.tmdbId}
                  value={request.tmdbId}
                  disabled
                />
              ) : (
                <TmdbContentAutocomplete
                  kind="tvShow"
                  value={request.tmdbId}
                  selectedTitle={tvShow.title}
                  selectedYear={tvShow.year}
                  label={t.tmdbSearch.linkTvShowLabel}
                  helperText={labels.tmdbLinkHelp}
                  matchCriteria={{
                    imdbId: tvShow.imdbId,
                    title: tvShow.title,
                    year: tvShow.year,
                  }}
                  loading={isLinkingTvShow}
                  disabled={isTvShowLinked || previewMutation.isPending}
                  onChange={(tmdbId) => setRequestField('tmdbId', tmdbId)}
                />
              )}
            </div>}
            <Input label={labels.seasonFrom} type="number" min="1" value={request.seasonFrom}
              onChange={(event) => setRequestField('seasonFrom', Number(event.target.value))} />
            <Input label={labels.seasonTo} type="number" min="1" value={request.seasonTo}
              onChange={(event) => setRequestField('seasonTo', Number(event.target.value))} />
            <Input label={labels.episodeFrom} type="number" min="1" value={request.episodeFrom}
              onChange={(event) => setRequestField('episodeFrom', Number(event.target.value))} />
            <Input label={labels.episodeTo} type="number" min="1" value={request.episodeTo}
              onChange={(event) => setRequestField('episodeTo', Number(event.target.value))} />
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">{labels.quality}</label>
              <select className={`${fieldClass} h-10`} value={request.quality}
                onChange={(event) => setRequestField('quality', event.target.value as BulkEpisodeQuality)}>
                {qualityOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </div>
            {source === 'generated' && (
              <Input label={labels.defaultDuration} type="number" min="1" max="300" value={defaultDuration}
                onChange={(event) => setDefaultDuration(Number(event.target.value))} />
            )}
          </div>

          {source === 'generated' && (
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <Input
                label={labels.englishTitleTemplate}
                helperText={labels.titleTemplateHelp}
                maxLength={200}
                value={englishTitleTemplate}
                onChange={(event) => setEnglishTitleTemplate(event.target.value)}
              />
              <Input
                label={labels.spanishTitleTemplate}
                helperText={labels.titleTemplateHelp}
                maxLength={200}
                value={spanishTitleTemplate}
                onChange={(event) => setSpanishTitleTemplate(event.target.value)}
              />
            </div>
          )}

          <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-[minmax(360px,2fr)_1fr_1fr_auto] xl:items-start">
            <Input
              label={labels.linkTemplate}
              placeholder="https://host/show.S[ss]E[eee].mp4"
              helperText={labels.linkHelp}
              error={linkTemplateError}
              value={request.linkTemplate}
              onChange={(event) => setRequestField('linkTemplate', event.target.value)}
            />
            <MultiSelect label={labels.audioLanguages} options={languageOptions}
              value={request.audioLanguages}
              onChange={(value) => setRequestField('audioLanguages', value as Language[])} />
            <MultiSelect label={labels.subtitleLanguages} options={languageOptions}
              value={request.subtitleLanguages}
              onChange={(value) => setRequestField('subtitleLanguages', value as Language[])} />
            <Button className="w-full xl:mt-6" onClick={handlePreview} loading={isPreviewLoading}
              disabled={(source === 'tmdb' && !request.tmdbId) || !request.linkTemplate
                || !hasValidEpisodePlaceholder || (source === 'generated' && !hasValidTitleTemplates)
                || isLinkingTvShow}>
              {!isPreviewLoading && <RefreshCw className="mr-2 h-4 w-4" />}
              {source === 'generated' ? labels.generatePreview : labels.preview}
            </Button>
          </div>
        </section>

        {episodes.length > 0 && (
          <section className="mt-5 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
              <div>
                <h2 className="font-medium text-gray-900">{labels.previewTitle}</h2>
                <p className="text-sm text-gray-500">
                  {labels.selected.replace('{selected}', String(selectedEpisodes.length)).replace('{total}', String(episodes.length))}
                  {existingCount > 0 ? ` · ${labels.existing.replace('{count}', String(existingCount))}` : ''}
                </p>
              </div>
              <Button onClick={handleCreate} loading={createMutation.isPending} disabled={selectedEpisodes.length === 0}>
                <Upload className="mr-2 h-4 w-4" />
                {labels.create.replace('{count}', String(selectedEpisodes.length))}
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-[1500px] w-full table-fixed border-collapse text-sm">
                <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
                  <tr>
                    <th className="w-12 px-3 py-2"><span className="sr-only">{labels.include}</span></th>
                    <th className="w-20 px-2 py-2">{labels.season}</th>
                    <th className="w-20 px-2 py-2">{labels.episode}</th>
                    <th className="w-64 px-2 py-2">{labels.englishTitle}</th>
                    <th className="w-64 px-2 py-2">{labels.spanishTitle}</th>
                    <th className="w-36 px-2 py-2">{labels.airDate}</th>
                    <th className="w-24 px-2 py-2">{labels.duration}</th>
                    <th className="w-20 px-2 py-2">{labels.rating}</th>
                    <th className="w-[360px] px-2 py-2">{labels.url}</th>
                    <th className="w-64 px-2 py-2">{labels.synopsis}</th>
                    <th className="w-12 px-2 py-2"><span className="sr-only">{labels.remove}</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {episodes.map((episode, index) => {
                    const selected = selectedKeys.has(rowKey(episode));
                    return (
                      <tr key={rowKey(episode)} className={selected ? 'bg-white' : 'bg-gray-50 opacity-60'}>
                        <td className="px-3 py-2 text-center">
                          <input type="checkbox" checked={selected} onChange={() => toggleSelected(episode)}
                            aria-label={labels.include} className="h-4 w-4 rounded border-gray-300" />
                        </td>
                        <td className="px-2 py-2"><input className={fieldClass} type="number" min="1" value={episode.seasonNumber}
                          onChange={(event) => updateEpisode(index, 'seasonNumber', Number(event.target.value))} /></td>
                        <td className="px-2 py-2"><input className={fieldClass} type="number" min="1" value={episode.episodeNumber}
                          onChange={(event) => updateEpisode(index, 'episodeNumber', Number(event.target.value))} /></td>
                        <td className="px-2 py-2"><input className={fieldClass} maxLength={200} value={episode.title}
                          onChange={(event) => updateEpisode(index, 'title', event.target.value)} /></td>
                        <td className="px-2 py-2"><input className={fieldClass} maxLength={200} value={episode.spanishTitle || ''}
                          onChange={(event) => updateEpisode(index, 'spanishTitle', event.target.value)} /></td>
                        <td className="px-2 py-2"><input className={fieldClass} type="date" value={episode.airDate || ''}
                          onChange={(event) => updateEpisode(index, 'airDate', event.target.value)} /></td>
                        <td className="px-2 py-2"><input className={fieldClass} type="number" min="1" max="300" value={episode.durationMinutes || ''}
                          onChange={(event) => updateEpisode(index, 'durationMinutes', Number(event.target.value))} /></td>
                        <td className="px-2 py-2"><input className={fieldClass} type="number" min="0" max="10" step="0.1" value={episode.rating || 0}
                          onChange={(event) => updateEpisode(index, 'rating', Number(event.target.value))} /></td>
                        <td className="px-2 py-2">
                          {(() => {
                            const url = episode.links?.[0]?.url || '';
                            const validation = linkValidations[url];
                            return (
                              <ValidatedEpisodeLinkInput
                                value={url}
                                validation={validation}
                                onChange={(nextUrl) => updateLink(index, nextUrl)}
                              />
                            );
                          })()}
                        </td>
                        <td className="px-2 py-2">
                          <button type="button" onClick={() => setDetailsIndex(index)}
                            title={episode.spanishSynopsis || episode.englishSynopsis || labels.details}
                            className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-gray-600 hover:bg-gray-100 hover:text-gray-900">
                            <Eye className="h-3.5 w-3.5 flex-none" />
                            <span className="block min-w-0 truncate">
                              {episode.spanishSynopsis || episode.englishSynopsis || labels.view}
                            </span>
                          </button>
                        </td>
                        <td className="px-2 py-2">
                          <button type="button" onClick={() => removeEpisode(index)} title={labels.remove}
                            className="p-1 text-red-600 hover:text-red-800"><Trash2 className="h-4 w-4" /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>

      <Modal isOpen={detailsEpisode != null} onClose={() => setDetailsIndex(null)} title={labels.detailsTitle} size="2xl">
        {detailsEpisode && detailsIndex != null && (
          <div className="space-y-4">
            <Input label={labels.coverImage} value={detailsEpisode.coverImageUrl || ''}
              onChange={(event) => updateEpisode(detailsIndex, 'coverImageUrl', event.target.value)} />
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">{labels.englishSynopsis}</label>
              <textarea className={`${fieldClass} min-h-32`} maxLength={5000} value={detailsEpisode.englishSynopsis || ''}
                onChange={(event) => updateEpisode(detailsIndex, 'englishSynopsis', event.target.value)} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">{labels.spanishSynopsis}</label>
              <textarea className={`${fieldClass} min-h-32`} maxLength={5000} value={detailsEpisode.spanishSynopsis || ''}
                onChange={(event) => updateEpisode(detailsIndex, 'spanishSynopsis', event.target.value)} />
            </div>
            <div className="flex justify-end"><Button onClick={() => setDetailsIndex(null)}>{labels.done}</Button></div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default BulkEpisodeImport;
